import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FieldValues, UseFormReturn, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import { getApiErrorResponse } from '@app/services/api';
import { EmailDesignContent, MessageDesignContent } from '@app/models';
import { MessageTemplateChannel } from '@app/utils/consts';
import { useResolvedDesignTemplate } from './useResolvedDesignTemplate';

// Form fields holding the linked template id; `null` = business default
export const TEMPLATE_ID_FIELDS = {
  [MessageTemplateChannel.EMAIL]: 'emailTemplateId',
  [MessageTemplateChannel.SMS]: 'smsTemplateId',
} as const;

// Custom design of a channel as TemplateList stores it: the design content and the HTML/text built from it
export const CUSTOM_DESIGN_FIELDS = {
  [MessageTemplateChannel.EMAIL]: { content: 'emailDesignContent', html: 'updatedEmailTemplate' },
  [MessageTemplateChannel.SMS]: { content: 'messageDesignContent', html: 'updatedMessageTemplate' },
} as const;

const CHANNEL_NAMES = { [MessageTemplateChannel.EMAIL]: 'email', [MessageTemplateChannel.SMS]: 'text' } as const;

type EditingDesign = { channel: MessageTemplateChannel.EMAIL; content: EmailDesignContent } | { channel: MessageTemplateChannel.SMS; content: MessageDesignContent };

// TemplateList fills unset fields with '' and may drop empty ones, so treat missing, null and '' alike
const isSameDesign = (a: object, b: object) => {
  const first = a as Record<string, unknown>;
  const second = b as Record<string, unknown>;

  return [...new Set([...Object.keys(first), ...Object.keys(second)])].every((key) => (first[key] ?? '') === (second[key] ?? ''));
};

const readStorage = (storageKey: string) => {
  try {
    return JSON.parse(localStorage.getItem(storageKey) ?? '{}');
  } catch {
    return {};
  }
};

// Template picker state for the automation/campaign create forms. TemplateList reads and writes the custom design in
// localStorage, while the forms also keep a copy and write it back on Back/submit, so both are always updated together here
export const useDesignTemplateFields = (form: UseFormReturn<FieldValues>, storageKey: string) => {
  const queryClient = useQueryClient();
  // `wasCustom`: the editor was opened on an existing custom design rather than on the template
  const [editingDesign, setEditingDesign] = useState<(EditingDesign & { wasCustom: boolean }) | null>(null);
  const [previewChannel, setPreviewChannel] = useState<MessageTemplateChannel | null>(null);

  const emailTemplateId = (useWatch({ control: form.control, name: TEMPLATE_ID_FIELDS.EMAIL }) as string | null | undefined) ?? null;
  const smsTemplateId = (useWatch({ control: form.control, name: TEMPLATE_ID_FIELDS.SMS }) as string | null | undefined) ?? null;
  const customEmailDesign = useWatch({ control: form.control, name: CUSTOM_DESIGN_FIELDS.EMAIL.content }) as EmailDesignContent | null | undefined;
  const customMessageDesign = useWatch({ control: form.control, name: CUSTOM_DESIGN_FIELDS.SMS.content }) as MessageDesignContent | null | undefined;
  const emailDesign = useResolvedDesignTemplate(MessageTemplateChannel.EMAIL, emailTemplateId, customEmailDesign);
  const smsDesign = useResolvedDesignTemplate(MessageTemplateChannel.SMS, smsTemplateId, customMessageDesign);

  const setTemplateId = (channel: MessageTemplateChannel, templateId: string | null) => form.setValue(TEMPLATE_ID_FIELDS[channel], templateId, { shouldDirty: true });

  // Without a custom design the linked template (or business default) is sent
  const resetCustomDesign = (channel: MessageTemplateChannel) => {
    const { content, html } = CUSTOM_DESIGN_FIELDS[channel];
    form.setValue(content, null, { shouldDirty: true });
    form.setValue(html, null, { shouldDirty: true });

    try {
      localStorage.setItem(storageKey, JSON.stringify({ ...readStorage(storageKey), [content]: null, [html]: null }));
    } catch {
      // ignore localStorage errors
    }
  };

  const startEditing = (design: EditingDesign) => setEditingDesign({ ...design, wasCustom: Boolean(form.getValues(CUSTOM_DESIGN_FIELDS[design.channel].content)) } as EditingDesign & { wasCustom: boolean });

  // TemplateList writes the design to storage only on "Save", so storage tells whether it was saved
  const closeDesignEditor = () => {
    if (editingDesign) {
      const { content, html } = CUSTOM_DESIGN_FIELDS[editingDesign.channel];
      const stored = readStorage(storageKey);

      // Saved without changes: keep following the template, so later edits of the template still reach this channel
      if (!editingDesign.wasCustom && stored[content] && isSameDesign(stored[content], editingDesign.content)) {
        resetCustomDesign(editingDesign.channel);
      } else {
        form.setValue(content, stored[content] ?? null, { shouldDirty: true });
        form.setValue(html, stored[html] ?? null, { shouldDirty: true });
      }
    }
    setEditingDesign(null);
  };

  // The linked template was deleted (the backend has unlinked it everywhere), so the form falls back to the default as well
  useEffect(() => {
    [
      { channel: MessageTemplateChannel.EMAIL, isMissing: emailDesign.isMissing },
      { channel: MessageTemplateChannel.SMS, isMissing: smsDesign.isMissing },
    ].forEach(({ channel, isMissing }) => {
      if (!isMissing) return;

      form.setValue(TEMPLATE_ID_FIELDS[channel], null, { shouldDirty: true });
      toast.warning(
          `The selected ${CHANNEL_NAMES[channel]} template was deleted. The business default is used instead.`,
          { autoClose: false, toastId: `deleted-template-${channel}` }
      );
    });
  }, [emailDesign.isMissing, smsDesign.isMissing, form]);

  // Save rejected because a linked template no longer exists (400 with `details: { emailTemplateId | smsTemplateId }`).
  // The channel falls back to the default and the user saves again, so nothing is sent with a design they didn't see
  const handleTemplateLinkError = (error: unknown) => {
    const { status, data } = getApiErrorResponse<{ details?: Record<string, unknown> }>(error);
    const staleChannels = status === 400 ? [MessageTemplateChannel.EMAIL, MessageTemplateChannel.SMS].filter((channel) => data?.details?.[TEMPLATE_ID_FIELDS[channel]] !== undefined) : [];
    if (!staleChannels.length) return false;

    staleChannels.forEach((channel) => form.setValue(TEMPLATE_ID_FIELDS[channel], null, { shouldDirty: true }));
    queryClient.invalidateQueries({ queryKey: ['message-templates'] });
    toast.warning(
      `The selected ${staleChannels.map((channel) => CHANNEL_NAMES[channel]).join(' and ')} template no longer exists, so the business default is selected now. Review the templates and save again.`,
      { autoClose: false }
    );

    return true;
  };

  return {
    emailTemplateId,
    smsTemplateId,
    customEmailDesign,
    customMessageDesign,
    emailDesign,
    smsDesign,
    setTemplateId,
    resetCustomDesign,
    editingDesign,
    startEditing,
    closeDesignEditor,
    handleTemplateLinkError,
    previewChannel,
    setPreviewChannel,
  };
};
